// Library/Package Imports

import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"

// Design/Component Imports

import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Select,
  MenuItem,
  ThemeProvider,
  Checkbox,
  TextField,
  IconButton,
  Menu,
} from "@mui/material"

import EditIcon from "../../../../assets/icons/edit.svg"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"

// Local Imports

import { CustomCell, FormDialog, customTheme } from "./Atom"
import { setLoading } from "../../../redux/slices/appSlice"
import { clearLocalstorage, getTokenLocal } from "../../../../helper/utils"
import {
  AddNewBucketColumns,
  DefaultMetricHeaders,
  MetricBlueprintDropdownOptions,
  permissions,
} from "../../../../config/constants"
import {
  setMetricBMTableData,
  setSelectedBlueprintMetrics,
} from "../../../redux/slices/metricSlice"
import DraggableMetrics from "../Row/Reusable"
import MetricConfirmation from "../../Modal/MetricConfirmation"
import EditBenchmark from "../../Modal/EditBenchmark"
import { useResponseHandle } from "../../../hooks"
import { useNavigate } from "react-router-dom"
import EditMetricModal from "../../Modal/EditMetric"

import BlueprintAPI from "../../../../api/blueprint"
import { colors } from "../../../../styles/commonStyles"
import { fetchAuditData } from "./../../../pages/Audit/apiUtils"

// Blueprint API class instance

const blueprintAPI = new BlueprintAPI()

const MetricBlueprintTable = (props: any) => {
  const { fetchMetricTabs, addNewMetric } = props

  const token: any = getTokenLocal()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)

  const allPerms = useSelector((state: any) => {
    return state.rolesAndPerms.userPerms
  })
  const tabs = useSelector((state: any) => {
    return state.metric.tabs
  })

  const selectedTab = useSelector((state: any) => {
    return state.metric.selectedTab
  })

  const isLoading = useSelector((state: any) => {
    return state.app.loading
  })

  const [localCheckedArray, setLocalCheckedArray] = useState<any>([])
  const [typeNewMetricName, setTypeNewMetricName] = useState(false)
  const [newMetricName, setNewMetricName] = useState("")
  const [openDialog, setOpenDialog] = useState<boolean>(false)

  const [editMetric, setEditMetric] = useState({
    new: "",
    old: "",
    id: null,
  })
  const [delMetric, setDelMetric] = useState({})
  const [editBenchmarkOpen, setEditBenchmarkOpen] = useState<boolean>(false)
  const [benchmark, setBenchmark] = useState({
    oldValue: "",
    newValue: "",
    metricId: null,
    goal_type: "",
  })

  const [openMetricEditDialog, setopenMetricEditDialog] =
    useState<boolean>(false)

  const [type, setType] = useState("")
  const MetricHeaders = DefaultMetricHeaders

  useEffect(() => {
    dispatch(setMetricBMTableData(tabs[selectedTab]?.metrics))
  }, [selectedTab, tabs])

  const metricBucketId = tabs[selectedTab]?.metric_bucket_id

  const handleDropdownValueChange = async (
    metricId: any,
    columnId: any,
    value: any,
  ) => {
    dispatch(setLoading(true))

    value = value == "N/A" ? "" : value

    if (token) {
      const response = await blueprintAPI.updateMetricValueType(token, {
        metricId,
        columnId,
        value,
      })

      if (
        response?.data?.status_code != 200 &&
        response?.data?.status_code != 401
      ) {
        toast.error(response?.data?.display_message)
      } else {
        toast.success(response?.data?.display_message)
      }
    }

    await fetchMetricTabs()
    dispatch(setLoading(false))
  }

  const handleGoalTypeValueChange = async (metricId: any, value: any) => {
    dispatch(setLoading(true))

    value = value == "N/A" ? "" : value

    if (token) {
      const response = await blueprintAPI.updateGoalType(token, {
        metricId,
        value,
      })

      if (
        response?.data?.status_code != 200 &&
        response?.data?.status_code != 401
      ) {
        toast.error(response?.data?.display_message)
      } else {
        toast.success(response?.data?.display_message)
      }
    }

    await fetchMetricTabs()
    dispatch(setLoading(false))
  }

  const handleMetricSelection = (
    event: any,
    metricId: any,
    metricName: any,
  ) => {
    let localCheckedArrayTemp = [...localCheckedArray]

    if (event.target.checked == true) {
      setLocalCheckedArray([...localCheckedArray, metricId])
    } else if (event.target.checked == false) {
      localCheckedArrayTemp = localCheckedArrayTemp.filter(
        (item) => item !== metricId,
      )
      setLocalCheckedArray([...localCheckedArrayTemp])
    }

    dispatch(
      setSelectedBlueprintMetrics({
        isChecked: event.target.checked,
        metric_id: metricId,
        metric_bucket_id: metricBucketId,
        metric_name: metricName,
      }),
    )
  }

  function isChecked(metricId: any) {
    if (localCheckedArray.includes(metricId)) {
      return true
    }
    return false
  }

  useEffect(() => {
    setLocalCheckedArray([])
  }, [isLoading])

  // Metric edit states
  const [currentActiveEditMetricName, setCurrentActiveEditMetricName] =
    useState(null)
  const [localMetricNameValue, setLocalMetricNameValue] = useState()

  // end state

  const handleEditBenchmarkOpen = (
    metricId: any,
    oldValue: any,
    goal_type: string,
  ) => {
    setBenchmark({
      ...benchmark,
      oldValue: oldValue,
      metricId: metricId,
      goal_type,
    })
    setEditBenchmarkOpen(true)
  }

  // Metric name edit handlers
  const closeEditMetricNameDialog = () => setopenMetricEditDialog(false)
  const openEditMetricNameDialog = () => setopenMetricEditDialog(true)
  const updateNewMetricName = (val: string) =>
    setEditMetric((prev) => ({ ...prev, new: val }))

  const handleMetricNameEditOpen = (
    oldMetricName: any,
    metricName: any,
    metricId: any,
  ) => {
    if (metricName == "") {
      setCurrentActiveEditMetricName(null)
      return
    }

    if (metricName == oldMetricName) {
      setCurrentActiveEditMetricName(null)
      return
    }

    // setType("edit")
    // setEditMetric({
    //   oldMetricName,
    //   metricName,
    //   metricId,
    // })
    // setOpen(true)
  }
  const handleMetricNameEdit = (editState: any) => {
    // setCurrentActiveEditMetricName(index)
    openEditMetricNameDialog()
    // setLocalMetricNameValue(currenValue)
    setEditMetric(editState)
  }
  const handleMetricNameChange = (value: any) => {
    setLocalMetricNameValue(value)
  }

  const handleMetricNameChangeSubmit = async () => {
    if (!editMetric.new) {
      setCurrentActiveEditMetricName(null)
      return
    }

    if (token) {
      const response = await blueprintAPI.updateMetricName(token, {
        metricId: editMetric.id,
        value: editMetric.new,
      })

      if (
        response?.data?.status_code != 200 &&
        response?.data?.status_code != 401
      ) {
        toast.error(response?.data?.display_message)
      } else {
        setCurrentActiveEditMetricName(null)
        toast.success(response?.data?.display_message)
      }
    }

    await fetchMetricTabs()
  }
  // end handlers

  const handleEditBenchmark = async () => {
    dispatch(setLoading(false))

    const payload =
      benchmark.goal_type === "%"
        ? parseFloat(benchmark?.newValue) / 100
        : benchmark?.newValue

    const response = await blueprintAPI.updateMetricBenchmark(token, {
      metricId: benchmark?.metricId,
      value: payload,
    })

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: true,
      permError: response?.data?.display_message,
    })

    if (result?.isAuthorized) {
      await fetchMetricTabs()
    } else {
      // clearLocalstorage()
      // navigate("/auth")
    }

    dispatch(setLoading(false))
  }

  function onlyLettersAndSpaces(str: string) {
    return /^[A-Za-z\s]*$/.test(str)
  }

  const handleNewMetricBlur = () => {
    if (newMetricName?.trim() == "" || newMetricName == undefined) {
      setTypeNewMetricName(false)
      return
    }

    setType("new")

    setOpen(true)
  }

  const handleCreateNewMetric = () => {
    const metricBucketId = tabs[selectedTab].metric_bucket_id

    if (newMetricName?.trim() == "" || newMetricName == undefined) {
      setTypeNewMetricName(false)
      setNewMetricName("")
      toast.error("Name can not be empty")
      return
    }

    const isValid = onlyLettersAndSpaces(newMetricName)

    if (isValid) {
      const res = addNewMetric(metricBucketId, newMetricName?.trim())
    } else {
      toast.error("Only alphabets are allowed")
      fetchMetricTabs()
    }

    setTypeNewMetricName(false)
    setNewMetricName("")
  }

  const handleEnterToCreateNewMetric = (event: any) => {
    if (event.key === "Enter") {
      event.preventDefault()

      setOpen(true)
    }
  }

  const handleMetricDeleteOpen = async (metricName: any, metricId: any) => {
    setType("del")
    setDelMetric({ metricName: metricName, metricId: metricId })
    setOpen(true)
  }

  const handleMetricDelete = async (metricId: any) => {
    if (token) {
      const response = await blueprintAPI.deleteMetric(token, metricId)

      if (
        response?.data?.status_code !== 200 &&
        response?.data?.status_code !== 401
      ) {
        toast.error(response?.data?.display_message)
      } else {
        toast.success(response?.data?.display_message)
      }
    }

    await fetchMetricTabs()
  }

  // dialog handlers
  const closeCreateMetricDialog = () => setOpenDialog(false)
  const openCreateMetricDialog = () => setOpenDialog(true)
  // new add
  const addNewMetricHandler = async (data: any, loading: any) => {
    loading(true)
    if (token) {
      const payload = {
        ...data,
        metric_bucket_id: metricBucketId,
        metric_benchmark:
          data?.goal_type == "%"
            ? JSON.stringify(parseFloat(data?.metric_benchmark) / 100)
            : data.metric_benchmark,
      }
      const result: any = await blueprintAPI.createNewMetric(token, payload)
      if (
        result?.data?.status_code != 200 &&
        result?.data?.status_code != 401
      ) {
        toast.error(result?.data?.display_message)
        fetchMetricTabs()
      } else {
        toast.success(result?.data?.display_message)
        fetchMetricTabs()
      }
    }
    loading(false)
    closeCreateMetricDialog()
  }

  const headersToCapsLock = ["Cytd", "Py", "pytd", "ttm"]

  const capitalizeMatchingWord = (sentence: string) => {
    // Split the sentence into words
    const words = sentence.split(/\s+/)
    // Iterate over each word in the sentence
    const capitalizedWords = words.map((word) => {
      // Check if the word exists in the headersToCapsLock array
      const matchIndex = headersToCapsLock.findIndex(
        (header) => header.toLowerCase() === word.toLowerCase(),
      )

      // If the word exists in headersToCapsLock, capitalize it
      if (matchIndex !== -1) {
        return word.toUpperCase()
      }

      // Check if the word is "percent" and if the sentence contains the specific phrases
      const lowerCaseWord = word.toLowerCase()
      if (
        (lowerCaseWord === "percent" || lowerCaseWord === "percent.") &&
        (sentence.toLowerCase().includes("current percent of total") ||
          sentence.toLowerCase().includes("current trend percent of total"))
      ) {
        return "%"
      }

      // Otherwise, return the word as is
      return word
    })
    // Join the words back into a sentence
    return capitalizedWords.join(" ")
  }

  return (
    <>
      <MetricConfirmation
        open={open}
        setOpen={setOpen}
        newMetricName={newMetricName}
        handleCreateNewMetric={handleCreateNewMetric}
        setNewMetricName={setNewMetricName}
        setTypeNewMetricName={setTypeNewMetricName}
        editMetric={editMetric}
        setCurrentActiveEditMetricName={setCurrentActiveEditMetricName}
        handleMetricNameChangeSubmit={handleMetricNameChangeSubmit}
        delMetric={delMetric}
        handleMetricDelete={handleMetricDelete}
        type={type}
      />
      <ThemeProvider theme={customTheme}>
        <TableContainer sx={{ height: "72vh" }}>
          <Table stickyHeader sx={{ tableLayout: "fixed" }}>
            {MetricHeaders?.length ? (
              <TableHead sx={{ height: "32px", padding: "6px", zIndex: 2 }}>
                <CustomCell
                  align="center"
                  key={0}
                  style={{ width: "217px" }}
                  sx={{
                    // boxShadow: "0px 3px 6px #00000029",
                    border: "0.5px solid #FFFFFF",
                    backgroundColor: colors.primaryOrange,
                    color: "#FFFFFF",
                    fontSize: "13px",
                    padding: 0,
                    fontWeight: "600",
                    zIndex: 3,
                  }}
                >
                  Metric Name
                </CustomCell>
                <CustomCell
                  align="center"
                  key={1}
                  style={{ width: "217px" }}
                  sx={{
                    // boxShadow: "0px 3px 6px #00000029",
                    border: "0.5px solid #FFFFFF",
                    backgroundColor: colors.primaryOrange,
                    color: "#FFFFFF",
                    fontSize: "13px",
                    padding: 0,
                    fontWeight: "600",
                    zIndex: 2,
                  }}
                >
                  Goal Type
                </CustomCell>
                <CustomCell
                  align="center"
                  key={2}
                  style={{ width: "217px" }}
                  sx={{
                    // boxShadow: "0px 3px 6px #00000029",
                    border: "0.5px solid #FFFFFF",
                    backgroundColor: colors.primaryOrange,
                    color: "#FFFFFF",
                    fontSize: "13px",
                    padding: 0,
                    fontWeight: "600",
                    zIndex: 1,
                  }}
                >
                  Metric Benchmark
                </CustomCell>
                {MetricHeaders?.map((ele: any, index: any) => {
                  return (
                    <CustomCell
                      align="center"
                      key={index + 3}
                      style={{ width: "200px" }}
                      sx={{
                        border: "0.5px solid #FFFFFF",
                        backgroundColor: colors.primaryOrange,
                        color: "#FFFFFF",
                        boxShadow: "",
                        fontSize: "13px",
                        padding: 0,
                        fontWeight: "600",
                        zIndex: 1,
                        minWidth: "135px",
                      }}
                    >
                      {capitalizeMatchingWord(ele?.column_name)}
                    </CustomCell>
                  )
                })}
              </TableHead>
            ) : (
              <></>
            )}
            <TableBody>
              {allPerms.includes(permissions?.BLUEPRINT_METRIC_MAINTENANCE) && (
                <TableRow key={0} sx={{ padding: 0, height: "60px" }}>
                  <CustomCell
                    align="left"
                    sx={{
                      color: "#3C77EF",
                      fontSize: "14px",
                      fontWeight: "600",
                      backgroundColor: "#fff",
                      overflow: "hidden",
                      // whiteSpace: "nowrap",
                      cursor: "pointer",
                      height: "100%",
                      width: "fit-content",
                      textOverflow: "ellipsis",
                      boxShadow: "3px 0 3px -2px #00000029",
                      zIndex: "2",
                      margin: "0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "auto 0px",
                        // justifyContent: "space-between",
                      }}
                    >
                      {/* {!typeNewMetricName ? ( */}
                      <Typography
                        onClick={() => {
                          // !tabs[selectedTab]?.isNew
                          // ? setTypeNewMetricName(true)
                          // : setTypeNewMetricName(false)
                          openCreateMetricDialog()
                        }}
                        fontSize={"14px"}
                        fontWeight={700}
                        sx={{ marginLeft: "10px", color: colors.primaryGreen }}
                      >
                        {"+ Add New Metric"}
                      </Typography>
                    </Box>
                  </CustomCell>
                  <CustomCell
                    align="center"
                    sx={{
                      // backgroundColor: row?.goal_type ? "#FFFFFF" : "#F7F7F7",
                      backgroundColor: "#F7F7F7",
                      zIndex: "1",
                      color: "#3C77EF",
                      fontSize: "14px",
                      fontWeight: "600",
                      overflow: "hidden",
                      height: "100%",
                      width: "fit-content",
                      textOverflow: "ellipsis",

                      borderRight: "1px solid #E4E4E4",

                      margin: "0",
                    }}
                  >
                    <Select
                      disabled
                      sx={{
                        width: "100%",
                        padding: "0px",
                        border: "none",
                        fontSize: "14px",
                        fontWeight: "bold",
                        "& .MuiOutlinedInput-notchedOutline": {
                          outline: "none",
                          border: "none",
                        },

                        color: "#000",
                        textAlign: "left",
                        paddingLeft: "10px",
                      }}
                      IconComponent={KeyboardArrowDownIcon}
                      value={"N/A"}
                      // onChange={(e) => {
                      // handleGoalTypeValueChange(row?.metric_id, e.target.value)
                      // }}
                    >
                      {MetricBlueprintDropdownOptions.map((option) => {
                        return (
                          <MenuItem
                            key={option}
                            sx={{
                              margin: "0px 10px 0px 10px",
                              color: "#000",
                              "& .MuiButtonBase-root": {
                                outline: "none",
                                border: "none",
                                backgroundColor: "white",
                                color: "#000",
                              },
                            }}
                            value={option}
                          >
                            {option}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </CustomCell>
                  <CustomCell
                    align="left"
                    sx={{
                      fontSize: "14px",
                      fontWeight: "600",
                      backgroundColor: "#F7F7F7",
                      color: "#909090",
                      overflow: "hidden",
                      height: "100%",
                      width: "fit-content",
                      textOverflow: "ellipsis",
                      borderRight: "1px solid #E4E4E4",
                      zIndex: "0",
                      margin: "0",
                    }}
                  >
                    {1 ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          padding: "auto 0px",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          fontSize={"14px"}
                          sx={{ marginLeft: "10px" }}
                        >
                          {/* {currentActiveEditBenchmark == index ? row.metric_benchmark : localBenchmarkValue} */}
                          {/* {row?.metric_benchmark} */}
                          {0.0}
                        </Typography>
                        <img
                          // aria-disabled={true}
                          src={EditIcon}
                          height={"12px"}
                          alt=""
                          style={{ cursor: "not-allowed" }}
                          // onClick={() => {
                          //   handleBenchmarkEdit(index, row?.metric_benchmark)
                          // }}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          padding: "auto 0px",
                          justifyContent: "space-between",
                        }}
                      >
                        <TextField
                          id="standard-basic"
                          onChange={(e) =>
                            setBenchmark({
                              ...benchmark,
                              newValue: e.target.value,
                            })
                          }
                          value={benchmark?.newValue}
                          variant="standard"
                          sx={{ margin: "0px", padding: "0" }}
                        />
                        {/* <Checkbox
                        sx={{ padding: "0", outine: "2px solid #B1B0B0" }}
                      /> */}
                        <CheckCircleIcon
                          sx={{ cursor: "pointer" }}
                          color="success"
                        />
                      </Box>
                    )}
                  </CustomCell>
                  {AddNewBucketColumns?.map((column: any, i: any) => {
                    return (
                      <TableCell
                        key={i}
                        align="center"
                        sx={{
                          borderRight: "1px solid #E4E4E4",
                          backgroundColor: column?.column_datatype
                            ? "#FFFFFF"
                            : "#F7F7F7",
                        }}
                      >
                        <Select
                          disabled
                          sx={{
                            width: "100%",
                            padding: "0px",
                            border: "none",
                            fontSize: "14px",
                            fontWeight: "bold",
                            "& .MuiOutlinedInput-notchedOutline": {
                              outline: "none",
                              border: "none",
                            },

                            color: "#000",
                            textAlign: "left",
                            paddingLeft: "10px",
                          }}
                          IconComponent={KeyboardArrowDownIcon}
                          value={column?.column_datatype || "N/A"}
                          // value={columnExists(row?.columns, id).column?.column_datatype == "" ? "N/A" : !(columnExists(row?.columns, id).column) ? "N/A" : columnExists(row?.columns, id)?.column?.column_datatype}
                          // onChange={(e) => {
                          //   handleDropdownValueChange(
                          //     row?.metric_id,
                          //     column?.column_id,
                          //     e.target.value,
                          //   )
                          // }}
                        >
                          {MetricBlueprintDropdownOptions.map((option) => {
                            return (
                              <MenuItem
                                key={option}
                                sx={{
                                  margin: "0px 10px 0px 10px",
                                  color: "#000",
                                  "& .MuiButtonBase-root": {
                                    outline: "none",
                                    border: "none",
                                    backgroundColor: "white",
                                    color: "#000",
                                  },
                                }}
                                value={option}
                              >
                                {option}
                              </MenuItem>
                            )
                          })}
                        </Select>
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              {
                <DraggableMetrics
                  handleDropdownValueChange={handleDropdownValueChange}
                  currentActiveEditMetricName={currentActiveEditMetricName}
                  isChecked={isChecked}
                  handleMetricSelection={handleMetricSelection}
                  localMetricNameValue={localMetricNameValue}
                  handleGoalTypeValueChange={handleGoalTypeValueChange}
                  handleEditBenchmarkOpen={handleEditBenchmarkOpen}
                  MetricBlueprintDropdownOptions={
                    MetricBlueprintDropdownOptions
                  }
                  setCurrentActiveEditMetricName={
                    setCurrentActiveEditMetricName
                  }
                  handleMetricNameEdit={handleMetricNameEdit}
                  handleMetricNameChange={handleMetricNameChange}
                  handleMetricNameChangeSubmit={handleMetricNameChangeSubmit}
                  handleMetricNameEditOpen={handleMetricNameEditOpen}
                  handleMetricDeleteOpen={handleMetricDeleteOpen}
                  fetchMetricTabs={fetchMetricTabs}
                />
              }
            </TableBody>
          </Table>
        </TableContainer>
      </ThemeProvider>
      <FormDialog
        open={openDialog}
        closeHandler={closeCreateMetricDialog}
        createHandler={addNewMetricHandler}
      />
      <EditBenchmark
        open={editBenchmarkOpen}
        setOpen={setEditBenchmarkOpen}
        handleEditBenchmark={handleEditBenchmark}
        benchmark={benchmark}
        setBenchmark={setBenchmark}
      />
      <EditMetricModal
        open={openMetricEditDialog}
        closeHandler={closeEditMetricNameDialog}
        metric={editMetric}
        setEditedMetric={handleMetricNameChangeSubmit}
        setNewMetric={updateNewMetricName}
      />
    </>
  )
}

export default MetricBlueprintTable
